<template>

  <div id="app" :class="{ 'app--sidenav': sidebarNav }">

    <!-- Sidebar layout (flag: VUE_APP_SIDEBAR_NAV=1 or localStorage nav.sidebar.enabled="1").

         Row: [SideNav] [ .app-main column: header top bar + router-view ]. -->

    <template v-if="sidebarNav">

      <side-nav v-if="showChrome" />

      <div class="app-main">

        <header-component v-if="showChrome" layout="side" />

        <router-view v-if="authRedirectHandled && (isMounted || $route.name === 'LockExtensionLanding')" :key="routerKey" />

      </div>

    </template>



    <!-- Original layout, byte-for-byte the previous markup. -->

    <template v-else>

      <header-component v-if="showChrome" />

      <router-view v-if="authRedirectHandled && (isMounted || $route.name === 'LockExtensionLanding')" :key="routerKey" />

    </template>



    <!-- Timeout Modal -->

    <transition name="fade">

      <div v-if="showTimeoutModal" class="timeout-overlay">

        <div class="timeout-modal">

          <div class="timeout-icon">⏱</div>

          <h2 class="timeout-title">Session Expired</h2>

          <p class="timeout-message">You've been inactive for 75 minutes and have been logged out.</p>

          <button class="timeout-btn" @click="handleTimeoutConfirm">Log Back In</button>

        </div>

      </div>

    </transition>

  </div>

</template>



<script>

import header from "./components/header.vue"

import SideNav from "./components/SideNav.vue"

import { isSidebarNavEnabled } from "@/helpers/navPrefs.js"

import apiService from "./api/api.service.js"

import auth from "./authConfig.js"

import bus from "@/helpers/appBus.js"



const TIMEOUT_MS = 75 * 60 * 1000

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click']



// Is this page load the emailed lock-expiration link (anonymous, loan id in the URL)?

//

// Read from window.location rather than $route ON PURPOSE. vue-router's initial navigation

// resolves asynchronously, so during created() — and the first render — $route.name is still

// undefined on a cold load. Deciding from $route there let the forced login fire, and let the

// header render and call acquireTokenResponse() in its mounted(), either of which redirects to

// Entra. The raw URL is correct from the first tick. History mode, so the path is in pathname.

function isEmailedExtensionLink() {

  try {

    if (!(window.location.pathname || '').toLowerCase().startsWith('/lock-extension-request')) {

      return false

    }



    const query = new URLSearchParams(window.location.search || '')

    return !!(query.get('loan_id') || query.get('loanId'))

  } catch {

    return false

  }

}



export default {

  components: {

    "header-component": header,

    "side-nav": SideNav

  },

  data() {

    return {

      // Read once at boot; a layout swap mid-session would remount every view.

      sidebarNav: isSidebarNavEnabled(),

      isMounted: false,

      authRedirectHandled: false,

      routerKey: 0,

      showTimeoutModal: false,

      timeoutTimer: null

    }

  },

  computed: {

    // Header + sidebar visibility, exactly the condition the header alone used before.

    showChrome() {

      return this.authRedirectHandled &&

        this.$route.name !== 'reverseMortgage' &&

        this.$route.name !== 'CommercialLoan' &&

        !this.isLockExtensionLandingWithLoan

    },

    // Only hide the header on LockExtensionLanding when a specific loan is

    // targeted (i.e. reached via an email link). Without a loan id it's just

    // the in-app "Find a Loan" search screen, which should keep the header

    // so users can navigate away.

    isLockExtensionLandingWithLoan() {

      // $route is read FIRST so this stays reactive to in-app navigation; the URL fallback covers

      // the cold load, when the router hasn't resolved yet. Don't reorder these.

      const matchesRoute = this.$route.name === 'LockExtensionLanding' && !!(

        this.$route.query?.loan_id ||

        this.$route.query?.loanId ||

        this.$route.params?.loanId

      )



      return matchesRoute || isEmailedExtensionLink()

    }

  },

  async created() {

    const msalAppInstance = await auth.init()



    msalAppInstance.handleRedirectPromise().then(async tokenRes => {

      if (!tokenRes) {

        if (!auth.user()) {

          // The lock expiration email links loan officers straight to the extension request page

          // with the loan id in the URL, and that link is the credential — business-accepted

          // 2026-08-05. Forcing a sign-in here is what used to bounce them to Entra (and, with

          // redirectUri '/', drop the loan). Every OTHER route still requires login.

          //

          // Checked off the URL, not $route — see isEmailedExtensionLink. The header is hidden for

          // this case (which also stops ITS acquireTokenResponse() from redirecting), and

          // router-view already renders LockExtensionLanding without waiting for isMounted (roles).

          // The page's own API calls go through its accessToken() helper, which returns '' rather

          // than triggering an interactive redirect when there is no account.

          if (isEmailedExtensionLink()) {

            this.isMounted = true

          } else {

            auth.login()

          }

        } else {

          await this.fetchData()

          bus.emit("auth-context-ready")

        }

      } else {

        await this.fetchData()

        bus.emit("auth-context-ready")

      }

    }).finally(() => {

      // header-component and router-view both mount their own MSAL calls

      // (header.vue's mounted() and page components like LockExtensionLanding.vue).

      // Letting them mount before handleRedirectPromise finishes lets those calls

      // race MSAL's own redirect-response processing and throw

      // "interaction_in_progress", since that processing is what clears MSAL's

      // in-progress flag after a redirect returns.

      this.authRedirectHandled = true

    })



    bus.on("auth-context-request", this.onAuthContextRequest)

    this.startActivityWatcher()

  },

  beforeUnmount() {

    bus.off("auth-context-request", this.onAuthContextRequest)

    this.clearActivityWatcher()

  },

  methods: {

    // ── Timeout ───────────────────────────────────────────────────────────────

    startActivityWatcher() {

      this.resetTimer()

      ACTIVITY_EVENTS.forEach(event =>

        window.addEventListener(event, this.onUserActivity, { passive: true })

      )

    },

    clearActivityWatcher() {

      clearTimeout(this.timeoutTimer)

      ACTIVITY_EVENTS.forEach(event =>

        window.removeEventListener(event, this.onUserActivity)

      )

    },

    onUserActivity() {

      if (this.showTimeoutModal) return

      this.resetTimer()

    },

    resetTimer() {

      clearTimeout(this.timeoutTimer)

      this.timeoutTimer = setTimeout(() => {

        this.showTimeoutModal = true

        this.clearActivityWatcher()

      }, TIMEOUT_MS)

    },

    handleTimeoutConfirm() {

      this.showTimeoutModal = false

      window.location.reload()

    },



    // ── Auth / data ───────────────────────────────────────────────────────────

    normalizeRoles(v) {

      if (!v) return []

      if (Array.isArray(v)) return v.map(x => String(x || "").trim()).filter(Boolean)

      return String(v).split(",").map(x => String(x || "").trim()).filter(Boolean)

    },

    rolesInclude(roles, key) {

      return roles.map(x => String(x || "").toLowerCase().trim()).includes(String(key || "").toLowerCase().trim())

    },

    async fetchRolesForAzureId(azureId, token) {

      const resp = await apiService.GetRoles(azureId, token)

      const data = resp?.data

      if (!data || !data.issuccess) return []

      return this.normalizeRoles(data.data)

    },

    async onAuthContextRequest() {

      this.isMounted = false

      apiService.clearApiCache()

      await this.fetchData()

      this.routerKey++

      this.isMounted = true

      bus.emit("auth-context-ready")

    },

    async fetchData() {

      try {

        const tokenResponse = await auth.acquireTokenResponse()

        if (!tokenResponse) throw new Error("Token response is null")



        const accessToken = tokenResponse.accessToken

        const authenticatedAzureId = localStorage.getItem("authenticated_azureid")

        if (!authenticatedAzureId) return



        const realRoles = await this.fetchRolesForAzureId(authenticatedAzureId, accessToken)

        localStorage.setItem("user_roles", JSON.stringify(realRoles))



        const hasImpersonationRole = this.rolesInclude(realRoles, "Impersonation")



        if (hasImpersonationRole) {

          const impersonationId = localStorage.getItem("imp_azureid")



          if (impersonationId && impersonationId !== "") {

            localStorage.setItem("azure_id", impersonationId)

            const impRoles = await this.fetchRolesForAzureId(impersonationId, accessToken)

            localStorage.setItem("imp_user_roles", JSON.stringify(impRoles))

            localStorage.setItem("active_user_roles", JSON.stringify(impRoles))

          } else {

            localStorage.setItem("azure_id", authenticatedAzureId)

            localStorage.setItem("active_user_roles", JSON.stringify(realRoles))

          }

        } else {

          localStorage.setItem("azure_id", authenticatedAzureId)

          localStorage.setItem("active_user_roles", JSON.stringify(realRoles))

        }

      } catch (error) {

        console.error("Error fetching data:", error)

      }



      // Fetch and cache azure profile data for the active user so that

      // header.vue, UserProfileMenu.vue, and dashboard getLar() don't

      // each fire their own request.

      try {

        const activeAzureId = localStorage.getItem("azure_id")

        const token = (await auth.acquireTokenResponse())?.accessToken

        if (activeAzureId && token) {

          const azResult = await apiService.GetUsersAzureData(activeAzureId, token)

          if (azResult?.data?.data) {

            localStorage.setItem("user_azure_data", JSON.stringify(azResult.data.data))

          }

        }

      } catch (e) {

        console.error("Error fetching azure data:", e)

      }



      this.isMounted = true

    }

  }

}

</script>



<style scoped>

@import "../src/assets/css/cgslight.css";

@import "../src/assets/css/site.css";



#app {

  font-family: Avenir, Helvetica, Arial, sans-serif;

  -webkit-font-smoothing: antialiased;

  -moz-osx-font-smoothing: grayscale;

  color: #2c3e50;

  height: 100vh;

  display: flex;

  flex-direction: column;

}



/* The app shell: this 100vh column is the height budget every routed view gets. The header

   takes what it needs, the view gets the rest — a view that wants to fit the window sizes

   itself with flex: 1 1 auto; min-height: 0 and scrolls its own content, rather than guessing

   at the header with calc(100vh - Npx) (its height moves with the rem breakpoints in

   cgslight.css, and guessing low is what put a scrollbar around the entire app).



   :deep() because these are the *rendered* children — `header-component` and `router-view`

   selectors, which used to live here, are compile-time tags that match no element at runtime. */

#app > :deep(.app-header) {

  flex: 0 0 auto;

}



/* ── Sidebar layout ── The 100vh column becomes a row; .app-main takes over the column role

   so every view keeps the exact same height budget it had before (flex: 1 1 auto; min-height: 0

   inside a flex column). Nothing in the views needs to change. */

#app.app--sidenav {

  flex-direction: row;

}



.app-main {

  flex: 1 1 auto;

  min-width: 0;

  min-height: 0;

  display: flex;

  flex-direction: column;

}



.app-main > :deep(.app-header) {

  flex: 0 0 auto;

}



/* Sidebar mode: the chat launchers live in the rail, so the draggable floating FABs from the

   dashboard are hidden here — one rule, regardless of which view renders them. The drawers

   they open still belong to that view (see ROUND2 notes for the bus wiring). */

#app.app--sidenav :deep(.cc-swiftai-fab),

#app.app--sidenav :deep(.cc-loanlaunch-fab) {

  display: none !important;

}





/* The three role dashboards (LoanOfficerDashboard/ProcessorDashboard/LoanOfficerAssistant

   Dashboard) size their root with a hardcoded `calc(100vh - 208px)` tuned for the

   dashboard-header block that used to sit above them. That block now teleports into the top

   bar (PageHeaderPortal), freeing that space — stretch the dashboard to actually fill it

   instead of leaving it blank. :deep() is required since .cc-dashboard lives in a child

   component, outside App.vue's own scoped styles. */

#app.app--sidenav :deep(.cc-dashboard) {

  height: 100%;

}



/* Header + sidebar are hidden on the same routes, so on those the main column is the whole row */

@media (max-width: 630px) {

  #app.app--sidenav {

    flex-direction: column;   /* SideNav hides itself here; back to the plain stack */

  }

}



/* ── Timeout modal ── */

.timeout-overlay {

  position: fixed;

  inset: 0;

  background: rgba(0, 0, 0, 0.55);

  backdrop-filter: blur(2px);

  display: flex;

  align-items: center;

  justify-content: center;

  z-index: 99999;

}



.timeout-modal {

  background: #ffffff;

  border-radius: 16px;

  padding: 40px 36px;

  max-width: 400px;

  width: 90%;

  text-align: center;

  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.2);

}



.timeout-icon {

  font-size: 48px;

  margin-bottom: 16px;

}



.timeout-title {

  font-size: 1.5rem;

  font-weight: 700;

  color: #0b1f3a;

  margin: 0 0 10px 0;

}



.timeout-message {

  font-size: 0.95rem;

  color: #5f6f84;

  margin: 0 0 28px 0;

  line-height: 1.5;

}



.timeout-btn {

  background: #0c2d5a;

  color: #ffffff;

  border: none;

  border-radius: 999px;

  padding: 12px 32px;

  font-size: 1rem;

  font-weight: 600;

  cursor: pointer;

  transition: background 0.2s ease;

}



.timeout-btn:hover {

  background: #1a4a8a;

}



.fade-enter-active,

.fade-leave-active { transition: opacity 0.18s ease; }

.fade-enter-from,

.fade-leave-to { opacity: 0; }

</style>